import React, { useRef, useEffect, memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { Product } from '../types';
import { gsap } from 'gsap';

interface ProductCardProps {
  product: Product;
  index?: number;
  from?: string;
}

const ProductCard: React.FC<ProductCardProps> = memo(({ product, index = 0, from = 'products' }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  useEffect(() => {
    if (!cardRef.current) return;
    
    const ctx = gsap.context(() => {
      gsap.set(cardRef.current, { opacity: 0, y: 20, willChange: 'transform, opacity' });
      
      gsap.to(cardRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        delay: index * 0.1,
        ease: 'power2.out',
        onComplete: () => {
          if (cardRef.current) {
            gsap.set(cardRef.current, { willChange: 'auto' });
          }
        }
      });
    }, cardRef);

    return () => {
      ctx.revert();
    };
  }, [index]);

  const formatPrice = React.useCallback((price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  }, []);

  return (
    <div
      ref={cardRef}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group overflow-hidden"
    >
      {/* Изображение */}
      <div className="relative overflow-hidden">
        <Link to={`/product/${product.id}`} state={{ from }}>
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
          />
        </Link>
        {!product.inStock && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
            Под заказ
          </div>
        )}
      </div>

      {/* Контент */}
      <div className="p-6">
        <Link to={`/product/${product.id}`} state={{ from }}>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200 cursor-pointer">
            {product.name}
          </h3>
        </Link>
        
        {/* Описание для десктопа */}
        <p className="hidden md:block text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">
          {product.description}
        </p>
        
        {/* Описание для мобильных с возможностью сворачивания */}
        <div className="md:hidden mb-4">
          <div className="text-gray-600 dark:text-gray-300 text-sm">
            <p className={isDescriptionExpanded ? '' : 'line-clamp-2'}>
              {product.description}
            </p>
          </div>
          
          {/* Кнопка сворачивания/разворачивания */}
          <button
            onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
            className="mt-2 text-primary-600 dark:text-primary-400 text-xs font-medium hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200 flex items-center space-x-1"
          >
            <span>{isDescriptionExpanded ? 'Свернуть' : 'Читать далее'}</span>
            {isDescriptionExpanded ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
          </button>
        </div>
        
        {/* Цена с боксом глаза справа для мобильных */}
        {product.price && (
          <div className="md:hidden flex items-center justify-between mb-4">
            <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
              {formatPrice(product.price)} ₽
            </span>
            
            {/* Бокс с иконкой глаза для мобильных */}
            <Link
              to={`/product/${product.id}`}
              state={{ from }}
              className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors duration-200"
            >
              <Eye className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </Link>
          </div>
        )}

        {/* Цена для десктопа */}
        {product.price && (
          <div className="hidden md:block mb-4">
            <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
              {formatPrice(product.price)} ₽
            </span>
          </div>
        )}

        {/* Кнопка "Подробнее" для десктопа */}
        <Link
          to={`/product/${product.id}`}
          className="hidden md:inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-200 group-hover:translate-x-1"
        >
          <span>Подробнее</span>
          <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
